import { useCallback, useEffect, useRef, useState } from "react";
import api from "./api";
import { safeGet, safeSet, safeRemove } from "./safeStorage";

const LOCAL_MS = 400;
const SYNC_MS = 2500;

const readLocal = (k) => {
  try { const raw = safeGet(`nexus_draft:${k}`); return raw ? JSON.parse(raw) : null; } catch { return null; }
};

/**
 * Auto-save a form value as a draft (device + optional account sync) and restore it on return.
 *  key       unique per form (+ per entity), e.g. "campaign-new", `apply:${id}`
 *  value     current form state (serialisable)
 *  onRestore called once with the saved draft when one exists
 *  isEmpty   (value) => bool — nothing worth saving / restoring
 *  sync      also persist to the server (/api/drafts) so it follows the user across devices
 *  enabled   wait until e.g. server data has loaded
 */
export function useDraft(key, { value, onRestore, isEmpty = () => false, sync = false, enabled = true }) {
  const [restoredAt, setRestoredAt] = useState(null);
  const [status, setStatus] = useState("idle"); // idle | saving | saved
  const readyRef = useRef(false);
  const localTimer = useRef(null);
  const syncTimer = useRef(null);
  const clearedRef = useRef(false);
  const onRestoreRef = useRef(onRestore);
  onRestoreRef.current = onRestore;
  const isEmptyRef = useRef(isEmpty);
  isEmptyRef.current = isEmpty;

  // 1. Restore on mount
  useEffect(() => {
    if (!enabled || !key) return;
    let alive = true;
    (async () => {
      let best = readLocal(key);
      if (sync) {
        try {
          const { data } = await api.get(`/drafts/${encodeURIComponent(key)}`);
          if (data?.data && (!best || (data.at || "") > (best.at || ""))) best = { data: data.data, at: data.at };
        } catch { /* offline or logged out — local only */ }
      }
      if (!alive) return;
      if (best?.data && !isEmptyRef.current(best.data)) {
        onRestoreRef.current?.(best.data);
        setRestoredAt(best.at || null);
      }
      readyRef.current = true;
    })();
    return () => { alive = false; };
    // eslint-disable-next-line
  }, [key, enabled]);

  // 2. Save on change
  useEffect(() => {
    if (!readyRef.current || !key || clearedRef.current) return;
    if (isEmptyRef.current(value)) return;
    setStatus("saving");
    clearTimeout(localTimer.current);
    localTimer.current = setTimeout(() => {
      const at = new Date().toISOString();
      safeSet(`nexus_draft:${key}`, JSON.stringify({ data: value, at }));
      setStatus("saved");
      if (sync) {
        clearTimeout(syncTimer.current);
        syncTimer.current = setTimeout(() => {
          api.put(`/drafts/${encodeURIComponent(key)}`, { data: value, at }).catch(() => {});
        }, SYNC_MS);
      }
    }, LOCAL_MS);
    return () => clearTimeout(localTimer.current);
    // eslint-disable-next-line
  }, [value, key]);

  const clear = useCallback(async () => {
    clearedRef.current = true;
    clearTimeout(localTimer.current);
    clearTimeout(syncTimer.current);
    safeRemove(`nexus_draft:${key}`);
    setRestoredAt(null);
    setStatus("idle");
    if (sync) { try { await api.delete(`/drafts/${encodeURIComponent(key)}`); } catch { /* ignore */ } }
    setTimeout(() => { clearedRef.current = false; }, 50);
  }, [key, sync]);

  return { restoredAt, status, clear };
}
